import { PointsCustomUtils } from "./PointsCustomUtils";
import makerjs from "makerjs";
import opentype from "opentype.js";
import * as THREE from "three";
import * as jsclipper from "js-clipper";
import SVGPathParser from "svg-path-parser";

function checkIntersectionBoolMakerModels(inModel1, inModel2) {
  let p1 = makerjs.cloneObject(inModel1);
  let p2 = makerjs.cloneObject(inModel2);
  let model = makerjs.model.combine(p1, p2, true, false, true, false);
  // path length
  let pathLength = makerjs.measure.modelPathLength(model);
  return pathLength > 0;
}

async function combineAllTextModel(inTextModel) {
  let modelsList = Object.keys(inTextModel.models);
  let models = inTextModel.models;
  if (modelsList.length < 2) {
    return inTextModel;
  }

  for (let index = 1; index < modelsList.length; index++) {
    const model = models[modelsList[index]];
    let prevModel = models[modelsList[index - 1]];

    let intersection;

    while (true) {
      intersection = checkIntersectionBoolMakerModels(model, prevModel);
      if (intersection) {
        break;
      }
      makerjs.model.moveRelative(model, [-1, 0]);
    }
  }
}

function addReferencePoint(inPoint, inObj3D, inColor) {
  let geometry = new THREE.SphereGeometry(0.4, 32, 32);
  let material = new THREE.MeshBasicMaterial({
    color: inColor ? inColor : Math.random() * 0xffffff,
  });
  let sphere = new THREE.Mesh(geometry, material);
  sphere.position.set(inPoint[0], inPoint[1], 0);
  if (inObj3D) {
    inObj3D.add(sphere);
  }
  return sphere;
}
export function convertModelToSvgPath(inModel) {
  let svgPath = makerjs.exporter.toSVGPathData(inModel);
  return svgPath;
}

function createMakerJsModelFromSvgPath(inPath) {
  let model = makerjs.importer.fromSVGPathData(inPath);
  return model;
}
function convertSvgPathToPoints(inPath) {
  return makerjs.path.toPoints(inPath, 100);
}
window.convertSvgPathToPoints = convertSvgPathToPoints;

export function createSVGTxt(
  text,
  size,
  fontBuffer,
  selectedFontLanguage,
  strokeWidth
) {
  return new Promise((resolve, reject) => {
    const bezierAccuracy = 0.5;
    let svg = null;

    if(!fontBuffer) return;
    let loadedFont;
    try {
      loadedFont = opentype.parse(fontBuffer); // ✅ Correct usage for ArrayBuffer
    } catch (err) {
      console.error("Error parsing font buffer:", err);
      reject(err);
      return;
    }

    try {
      const textModel = new makerjs.models.Text(
        loadedFont,
        text,
        size,
        false,
        false,
        bezierAccuracy,
        strokeWidth
      );
      const arabicModel = new makerjs.models.Text(
        loadedFont,
        text,
        size,
        false,
        false,
        bezierAccuracy,
        strokeWidth
      );

      const svgOfArabic = makerjs.exporter.toSVG(arabicModel);
      let combinedModel = textModel;

      const individualCharSVGAra = makerjs.exporter.toSVG(arabicModel);

      // Combine models
      combineAllTextModel(combinedModel); // <- Assuming this function exists
      const combinedModelForDotAttachment = makerjs.model.clone(combinedModel);

      const modelsList = Object.keys(combinedModel.models);
      let combinedModelforText = combinedModel.models[modelsList[0]];

      for (let index = 1; index < modelsList.length; index++) {
        const currentModel = makerjs.model.clone(
          combinedModel.models[modelsList[index]]
        );
        combinedModelforText = makerjs.model.combineUnion(
          combinedModelforText,
          currentModel
        );
      }

      const combinedModelCopy = makerjs.model.clone(combinedModelforText);
      svg = makerjs.exporter.toSVG(combinedModelCopy);

      // Convert model to path and parse it
      const svgPathData = convertModelToSvgPath(combinedModelCopy);
      const pathCommands = SVGPathParser(svgPathData);
      const clipper = new jsclipper.Clipper();
      const solution = new jsclipper.Paths();
      let clip = [];

      let minY = Number.MAX_VALUE;
      let maxY = Number.MIN_VALUE;

      pathCommands.forEach((command) => {
        if (command.code === "M" || command.code === "Z") {
          clipper.AddPath(clip, jsclipper.PolyType.ptSubject, true);
          clip = [];
        } else {
          clip.push({ X: command.x, Y: command.y });
          if (command.y < minY) minY = command.y;
          if (command.y > maxY) maxY = command.y;
        }
      });

      const height = (maxY - minY) / 4.46051428571;
      const differenceRatio = height / 3.500003564614746;

      clipper.Execute(jsclipper.ClipType.ctUnion, solution);

      const individualCharSVGEng = makerjs.exporter.toSVG(
        combinedModelForDotAttachment
      );

      resolve({
        svg,
        svgPathData,
        combinedModel,
        text,
        combinedModelCopy,
        combinedModelForDotAttachment,
        differenceRatio,
        height,
        svgOfArabic,
        arabicModel,
        individualCharSVGEng,
        individualCharSVGAra,
      });
    } catch (err) {
      console.error("SVG creation failed:", err);
      reject(err);
    }
  });
}

export function findChainPoints(inPoints, inChain1, inChain2, inObj3D) {
  let pointsUtils = new PointsCustomUtils(inPoints);

  let leftTopPoint = [pointsUtils.minX, pointsUtils.minY];
  let leftTop = makerjs.point.closest(leftTopPoint, inPoints);

  let rightTopPoint = [pointsUtils.maxX, pointsUtils.minY];
  let rightTop = makerjs.point.closest(rightTopPoint, inPoints);

  let leftTopSphere = addReferencePoint(leftTop, inObj3D, "red");
  let rightTopSphere = addReferencePoint(rightTop, inObj3D, "green");

  leftTopSphere.visible = false;
  rightTopSphere.visible = false;

  let leftWPoint = leftTopSphere.getWorldPosition(new THREE.Vector3());
  let rightWPoint = rightTopSphere.getWorldPosition(new THREE.Vector3());
  let centerWPoint = rightTopSphere.getWorldPosition(new THREE.Vector3());
  leftWPoint.x += 0.7;
  rightWPoint.x -= 0.7;
  leftWPoint.y -= 0.5;
  rightWPoint.y -= 0.3;

  return [leftWPoint, rightWPoint, centerWPoint];
}

export function scaleSVGPath(inPath, inScale) {
  let path = createMakerJsModelFromSvgPath(inPath);
  let scaledPath = makerjs.model.scale(path, inScale);
  return convertModelToSvgPath(scaledPath);
}
